import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const DEFAULT_WIDTH = 4;
const DEFAULT_HEIGHT = 3;

const TimelineImage = forwardRef(function TimelineImage(
  {
    alt,
    imageMeta,
    language = "EN",
    lightbox = false,
    onClick,
    onKeyDown,
    priority = false,
    src,
  },
  forwardedRef
) {
  const shellRef = useRef(null);
  const fullImageRef = useRef(null);
  const mountedRef = useRef(true);
  const [shouldLoad, setShouldLoad] = useState(priority || lightbox);
  const [fullLoaded, setFullLoaded] = useState(false);
  const [fullFailed, setFullFailed] = useState(false);
  const [blurFailed, setBlurFailed] = useState(false);

  const width = imageMeta?.width || DEFAULT_WIDTH;
  const height = imageMeta?.height || DEFAULT_HEIGHT;
  const isPortrait = height > width * 1.05;
  const srcSet = imageMeta?.sources
    ?.map((candidate) => `${candidate.src} ${candidate.width}w`)
    .join(", ");
  const sizes = isPortrait
    ? "(max-width: 768px) 47vw, (max-width: 1024px) 186px, 280px"
    : "(max-width: 768px) 70vw, 280px";

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setShouldLoad(priority || lightbox);
    setFullLoaded(false);
    setFullFailed(false);
    setBlurFailed(false);
  }, [lightbox, priority, src]);

  useEffect(() => {
    if (shouldLoad || !shellRef.current) return undefined;

    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: "800px 0px" }
    );
    observer.observe(shellRef.current);
    return () => observer.disconnect();
  }, [shouldLoad]);

  const revealDecodedImage = useCallback(async (image) => {
    if (!image?.naturalWidth) return;
    try {
      await image.decode?.();
    } catch {
      // A successful load can still be displayed when decode() is unavailable
      // or rejects because the browser already decoded the cached resource.
    }
    if (mountedRef.current) setFullLoaded(true);
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    const image = fullImageRef.current;
    if (image?.complete && image.naturalWidth > 0) {
      revealDecodedImage(image);
    }
  }, [revealDecodedImage, shouldLoad, src, srcSet]);

  const setShellRef = useCallback(
    (node) => {
      shellRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );

  const shellStyle = {
    "--timeline-image-ratio": `${width} / ${height}`,
  };
  if (lightbox) {
    shellStyle.width = `min(90vw, 1100px, ${width}px, ${
      (80 * width) / height
    }vh)`;
  }

  const interactiveProps =
    onClick && !fullFailed
      ? {
          onClick,
          onKeyDown,
          role: "button",
          tabIndex: 0,
        }
      : {};

  return (
    <span
      ref={setShellRef}
      className={`timeline-image-shell ${
        isPortrait ? "is-portrait" : "is-landscape"
      } ${lightbox ? "is-lightbox" : ""} ${
        fullLoaded ? "is-full-loaded" : ""
      } ${fullFailed ? "has-error" : ""}`.trim()}
      style={shellStyle}
      {...interactiveProps}
    >
      {shouldLoad && imageMeta?.blurSrc && !blurFailed && (
        <img
          aria-hidden="true"
          alt=""
          className="timeline-image-lqip"
          decoding="async"
          height={height}
          onError={() => setBlurFailed(true)}
          src={imageMeta.blurSrc}
          width={width}
        />
      )}
      {shouldLoad && !fullFailed && (
        <picture>
          {!lightbox && srcSet && (
            <source sizes={sizes} srcSet={srcSet} type="image/webp" />
          )}
          <img
            ref={fullImageRef}
            alt={alt}
            className={`timeline-event-image timeline-image-full ${
              isPortrait ? "is-portrait" : "is-landscape"
            }`}
            decoding="async"
            fetchPriority={priority ? "high" : "auto"}
            height={height}
            loading={priority || lightbox ? "eager" : "lazy"}
            onError={() => setFullFailed(true)}
            onLoad={(event) => revealDecodedImage(event.currentTarget)}
            src={src}
            width={width}
          />
        </picture>
      )}
      {fullFailed && (
        <span className="timeline-image-fallback" role="img" aria-label={alt}>
          {language === "CN" ? "图片暂时无法显示" : "Image unavailable"}
        </span>
      )}
    </span>
  );
});

export default TimelineImage;
